import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { projectsApi, handleApiError } from '@/services/api';
import { toast } from 'sonner';
import type { ProjectCreateInput } from '@/types';

const schema = z.object({
  name: z.string().min(1, 'Project name is required').max(200, 'Name too long'),
  description: z.string().optional(),
  use_case: z.enum([
    'family_law',
    'compliance_dora',
    'compliance_iso_27001',
    'compliance_esg',
    'compliance_soc2',
    'rfp_response',
    'security_questionnaire',
    'service_proposal'
  ], {
    required_error: 'Please select a use case',
  }),
  language: z.string().default('en'),
});

type FormData = z.infer<typeof schema>;

interface ProjectCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ProjectCreateDialog({ open, onOpenChange, onSuccess }: ProjectCreateDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const selectedUseCase = watch('use_case');
  const selectedLanguage = watch('language');

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      await projectsApi.create(data as ProjectCreateInput);
      toast.success(t('projects.projectCreated'));
      reset();
      onSuccess();
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t('projects.createTitle')}</DialogTitle>
          <DialogDescription>
            {t('projects.createDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 py-4">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                {t('projects.projectName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder={t('projects.projectNamePlaceholder')}
                {...register('name')}
                disabled={loading}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            {/* Use Case */}
            <div className="space-y-2">
              <Label htmlFor="use_case">
                {t('projects.useCase')} <span className="text-red-500">*</span>
              </Label>
              <Select
                onValueChange={(value) => setValue('use_case', value as FormData['use_case'])}
                value={selectedUseCase}
                disabled={loading}
              >
                <SelectTrigger id="use_case">
                  <SelectValue placeholder={t('projects.selectUseCase')} />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {/* Compliance Modules */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {t('projects.complianceCert')}
                  </div>
                  <SelectItem value="compliance_dora">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.compliance_dora')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.compliance_dora_desc')}
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="compliance_iso_27001">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.compliance_iso_27001')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.compliance_iso_27001_desc')}
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="compliance_esg">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.compliance_esg')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.compliance_esg_desc')}
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="compliance_soc2">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.compliance_soc2')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.compliance_soc2_desc')}
                      </span>
                    </div>
                  </SelectItem>
                  
                  {/* Sales Automation */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2 border-t">
                    {t('projects.salesAutomation')}
                  </div>
                  <SelectItem value="rfp_response">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.rfp_response')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.rfp_response_desc')}
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="security_questionnaire">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.security_questionnaire')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.security_questionnaire_desc')}
                      </span>
                    </div>
                  </SelectItem>
                  
                  {/* Service Proposals */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2 border-t">
                    {t('projects.serviceProposals')}
                  </div>
                  <SelectItem value="service_proposal">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.service_proposal')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.service_proposal_desc')}
                      </span>
                    </div>
                  </SelectItem>

                  {/* Legacy/Other */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2 border-t">
                    {t('projects.other')}
                  </div>
                  <SelectItem value="family_law">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('useCases.family_law')}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('useCases.family_law_desc')}
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.use_case && (
                <p className="text-sm text-destructive">{errors.use_case.message}</p>
              )}
            </div>

            {/* Document Language */}
            <div className="space-y-2">
              <Label htmlFor="language">{t('projects.documentLanguage')}</Label>
              <Select
                onValueChange={(value) => setValue('language', value)}
                value={selectedLanguage || 'en'}
                disabled={loading}
              >
                <SelectTrigger id="language">
                  <SelectValue placeholder={t('projects.selectLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="de">German (Deutsch)</SelectItem>
                  <SelectItem value="fr">French (Français)</SelectItem>
                  <SelectItem value="nl">Dutch (Nederlands)</SelectItem>
                  <SelectItem value="es">Spanish (Español)</SelectItem>
                  <SelectItem value="it">Italian (Italiano)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('projects.descriptionLabel')}</Label>
              <Textarea
                id="description"
                placeholder={t('projects.descriptionPlaceholder')}
                rows={3}
                {...register('description')}
                disabled={loading}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  {t('common.creating')}
                </>
              ) : (
                t('projects.createProject')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
